import React, { ReactElement } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Card,
  Col,
  OverlayTrigger,
  ProgressBar,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import moment from 'moment';
import useSWR from 'swr';
import { getMigrationReadinessReport } from 'api/MigrationReadinessReportAPI';
import { useHasRoles } from 'hooks/useHasRoles';
import { MigrationReadinessReport } from 'types/MigrationReadinessReport';

type DetailItem = {
  label: string
  value: React.ReactNode
  tooltip?: string
}

type CountItem = {
  label: string
  value: number
  tooltip?: string
}

type ListItem = {
  label: string
  count?: number
  values: string[]
  tooltip?: string
}

type CheckResult = {
  field: string
  message: string
}

const useMigrationReadinessReport = () => useSWR(
  ['migration-readiness-report'],
  async () => getMigrationReadinessReport({
    ignoreGlobalCatch: true,
  }),
  {
    shouldRetryOnError: false,
  },
);

const formatNullable = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'Not set';
  return `${value}`;
};

const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return 'Unknown';
  return value ? 'Yes' : 'No';
};

const formatDate = (value: string | null): string => {
  if (!value) return 'Not set';
  return moment(value).format('MMM D, YYYY');
};

const getErrorMessage = (error: any): string => (
  error?.response?.data?.Message
  || error?.response?.data?.message
  || 'An error occurred loading the migration readiness report.'
);

const percent = (part: number, total: number): number => {
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const hasText = (value: string | null | undefined): boolean => !!value?.trim();

const addCheck = (
  checks: CheckResult[],
  condition: boolean,
  field: string,
  message: string,
): void => {
  if (condition) {
    checks.push({ field, message });
  }
};

const generateMigrationChecks = (
  report: MigrationReadinessReport,
): { blockers: CheckResult[], warnings: CheckResult[] } => {
  const blockers: CheckResult[] = [];
  const warnings: CheckResult[] = [];
  const unsyncedProfileCount = report.UnsyncedResidents + report.UnsyncedStaff + report.UnsyncedFamily;

  addCheck(
    blockers,
    report.V1SyncedFacilityId === null || report.V1SyncedFacilityId === undefined,
    'V1 synced facility ID',
    'This account is not linked to a Go Icon facility. Current migration focus is V1-synced customers.',
  );
  addCheck(
    blockers,
    report.V1SyncTenantId === null || report.V1SyncTenantId === undefined,
    'V1 sync tenant ID',
    'This account does not have a V1 sync tenant configured. Current migration focus is V1-synced customers.',
  );
  addCheck(
    blockers,
    report.FutureScheduledBroadcastsCount > 0,
    'Future scheduled broadcasts',
    'Future scheduled broadcasts need a cutover plan or manual migration before switching stacks.',
  );
  addCheck(
    blockers,
    report.MarketingTemplateCount > 0,
    'Marketing templates',
    'Marketing templates are not fully supported on CaaS yet, including unsubscribe behavior.',
  );
  addCheck(
    blockers,
    report.LibraryContentCount > 0,
    'Library content',
    'Library content is not supported on CaaS yet and needs a product or migration decision.',
  );
  addCheck(
    blockers,
    report.TotalAutomaticEventsCount > 0,
    'Automatic events',
    'Automatic events are not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.AppointmentRemindersCount > 0,
    'Appointment reminders',
    'Appointment reminders are not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.WellnessChecksCount > 0,
    'Wellness checks',
    'Wellness checks differ between legacy communications and Engagement and need migration review.',
  );
  addCheck(
    blockers,
    report.InactivePeriodCount > 0,
    'Inactive periods',
    'Inactive-period rules are not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.DoNotCallBetweenCount > 0,
    'Do-not-call windows',
    'Do-not-call windows are not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.EnterpriseFeedback360SurveyCount > 0,
    'Enterprise Feedback360 surveys',
    'Enterprise Feedback360 surveys are not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.EnterpriseReportingEnabled,
    'Enterprise reporting',
    'Enterprise reporting is not supported on CaaS yet.',
  );
  addCheck(
    blockers,
    report.DischargedPatientRecordCount > 0,
    'Discharged patient records',
    'Discharged patient workflows are not supported on CaaS.',
  );

  addCheck(
    warnings,
    report.AutomaticLinkingEnabled !== true,
    'Automatic linking',
    'Manual or unknown user linking may mean users need access cleanup before migration.',
  );
  addCheck(
    warnings,
    report.UnlinkedUserCount > 0,
    'Unlinked users',
    'Some eligible users do not have a Go Icon association and may lose access after migration.',
  );
  addCheck(
    warnings,
    report.Status !== 'Active',
    'Status',
    'This account is not active. Confirm whether it should be migrated.',
  );
  addCheck(
    warnings,
    report.LoginType !== null && report.LoginType.toLowerCase() !== 'password',
    'Login type',
    'Non-password login may need separate authentication support review.',
  );
  addCheck(
    warnings,
    hasText(report.CallerId),
    'Caller ID',
    'Caller ID is supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    report.ThrottleSetting !== null && report.ThrottleSetting !== undefined,
    'Throttle setting',
    'Throttle settings are not currently supported on CaaS.',
  );
  addCheck(
    warnings,
    report.RepeatMessage === true,
    'Repeat message',
    'Repeat message is supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    report.TtsRate !== null && report.TtsRate !== undefined,
    'TTS rate',
    'TTS rate is supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    report.SendPostEventReport === true,
    'Post-event report',
    'Post-event reports are not currently supported on CaaS.',
  );
  addCheck(
    warnings,
    hasText(report.PostEventEmailRecipients),
    'Post-event recipients',
    'Post-event report recipients are configured and need migration review.',
  );
  addCheck(
    warnings,
    hasText(report.CommunityName) || hasText(report.SeniorsAlias) || hasText(report.StaffAlias)
      || hasText(report.FamilyAlias),
    'Account wording aliases',
    'Community name or user-type aliases may be referenced in message content and need migration review.',
  );
  addCheck(
    warnings,
    report.EmailSenderDomains.length > 0,
    'Email sender domains',
    'Sender domains are supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    !report.UniquePhoneNumber,
    'Unique sender number',
    'This account uses a shared sender number, which cannot be brought over to CaaS as-is.',
  );
  addCheck(
    warnings,
    report.TransferToNumberCount > 0,
    'Transfer-to numbers',
    'Transfer-to numbers are supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    unsyncedProfileCount > 0,
    'Unsynced profiles',
    'Profiles exist in legacy communications that are not synced to Go Icon and need review.',
  );
  addCheck(
    warnings,
    report.BroadcastTemplateCount > 0,
    'Broadcast templates',
    'Broadcast templates need to be imported or recreated for CaaS.',
  );
  addCheck(
    warnings,
    report.SmsAutoRepliesCount > 0,
    'SMS auto replies',
    'SMS auto replies are supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    report.VirtualMeetingsCount > 0,
    'Virtual meetings',
    'Virtual meetings are deprecated and are not planned for CaaS support.',
  );
  addCheck(
    warnings,
    report.CustomFieldCount > 0,
    'Custom fields',
    'Custom fields may need Engagement support or customer communication before migration.',
  );
  addCheck(
    warnings,
    report.ApiKeyCount > 0,
    'API keys',
    'Configured API keys may indicate comms-only integrations or external usage.',
  );
  addCheck(
    warnings,
    report.LifeDateFieldCount > 0 || report.ComplianceDateFieldCount > 0,
    'Date fields',
    'Lifedate or compliance fields may need Engagement support or customer communication before migration.',
  );
  addCheck(
    warnings,
    report.PhoneSimpleSurveyCount > 0,
    'Phone simple surveys',
    'Phone simple surveys are deprecated and are not planned for CaaS support.',
  );
  addCheck(
    warnings,
    report.ActivityAttendanceCount > 0,
    'Activity attendance',
    'Activity attendance is deprecated and is not planned for CaaS support.',
  );
  addCheck(
    warnings,
    report.AccushieldTemplateCount > 0,
    'Accushield templates',
    'Accushield templates are deprecated and are not planned for CaaS support.',
  );
  addCheck(
    warnings,
    report.Feedback360SurveyCount > 0,
    'Feedback360 surveys',
    'Feedback360 surveys are supported on CaaS, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    report.BroadcastGreetingFarewellEnabled,
    'Broadcast greeting/farewell',
    'Broadcast greeting and farewell messages are not currently supported on CaaS.',
  );
  addCheck(
    warnings,
    report.OnDemandGreetingFarewellEnabled,
    'On-demand greeting/farewell',
    'On-demand greeting and farewell messages are supported, but there is no automated migration support yet.',
  );
  addCheck(
    warnings,
    hasText(report.SeniorIntegrationBanner) || hasText(report.StaffIntegrationBanner)
      || hasText(report.FamilyIntegrationBanner),
    'Integration banners',
    'Integration banners may imply comms-only integrations that need investigation.',
  );
  addCheck(
    warnings,
    report.DailyScheduledReportCount > 0,
    'Daily scheduled reports',
    'Daily scheduled reports are not currently supported on CaaS and may be deprecated.',
  );
  addCheck(
    warnings,
    report.OnDemandUsageCount > 0,
    'On-demand usage',
    'On-demand functionality is supported on CaaS, but migration behavior and phone number usage may differ.',
  );
  addCheck(
    warnings,
    report.ReplyForwardingCount > 0,
    'Reply forwarding',
    'Reply forwarding is not currently supported on CaaS; SMS inbox may supersede this workflow.',
  );

  return { blockers, warnings };
};

const getTooltipId = (label: string): string => (
  `caas-migration-tooltip-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
);

const FieldLabel = ({ label, tooltip }: { label: string, tooltip?: string }): ReactElement => {
  if (!tooltip) return <span>{label}</span>;

  return (
    <>
      {label}
      {' '}
      <OverlayTrigger
        placement="top"
        overlay={(
          <Tooltip id={getTooltipId(label)}>
            {tooltip}
          </Tooltip>
        )}
      >
        <span
          aria-label={`${label} help`}
          className="text-muted"
          role="img"
          style={{ cursor: 'help' }}
        >
          <FontAwesomeIcon icon="question-circle" size="1x" />
        </span>
      </OverlayTrigger>
    </>
  );
};

const DetailList = ({ items }: { items: DetailItem[] }): ReactElement => (
  <dl className="row mb-0">
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <dt className="col-sm-5 text-muted font-weight-normal">
          <FieldLabel label={item.label} tooltip={item.tooltip} />
        </dt>
        <dd className="col-sm-7">{item.value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const CountGrid = ({ items }: { items: CountItem[] }): ReactElement => (
  <Row>
    {items.map((item) => (
      <Col md={4} lg={3} className="mb-3" key={item.label}>
        <div className="border rounded p-3 h-100 bg-light">
          <div className="h4 mb-1">{item.value.toLocaleString()}</div>
          <div className="text-muted">
            <FieldLabel label={item.label} tooltip={item.tooltip} />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

const ListCard = ({ item }: { item: ListItem }): ReactElement => (
  <Card className="mb-3 h-100">
    <Card.Header as="h5">
      <FieldLabel label={item.label} tooltip={item.tooltip} />
      {item.count !== undefined && (
        <span className="text-muted font-weight-normal">
          {' '}
          (
          {item.count.toLocaleString()}
          )
        </span>
      )}
    </Card.Header>
    <Card.Body>
      {item.values.length > 0 ? (
        <ul className="mb-0 pl-3">
          {item.values.map((value) => (
            <li key={value}>{value}</li>
          ))}
        </ul>
      ) : (
        <span className="text-muted">None</span>
      )}
    </Card.Body>
  </Card>
);

const CheckList = ({ checks }: { checks: CheckResult[] }): ReactElement => {
  if (checks.length === 0) {
    return <p className="text-muted mb-0">None found.</p>;
  }

  return (
    <ul className="mb-0 pl-3">
      {checks.map((check) => (
        <li key={`${check.field}-${check.message}`} className="mb-2">
          <strong>{check.field}</strong>
          :
          {' '}
          {check.message}
        </li>
      ))}
    </ul>
  );
};

const AutomatedChecksSummary = (
  { blockers, warnings }: { blockers: CheckResult[], warnings: CheckResult[] },
): ReactElement => {
  const hasFindings = blockers.length > 0 || warnings.length > 0;

  return (
    <Card className="mb-4">
      <Card.Header as="h5">Automated migration checks</Card.Header>
      <Card.Body>
        {!hasFindings && (
          <Alert variant="success" className="mb-4">
            No automated blockers or warnings were found for the current rule set.
          </Alert>
        )}
        <Alert variant={blockers.length > 0 ? 'danger' : 'secondary'}>
          <h5>
            Blockers
            {' '}
            (
            {blockers.length}
            )
          </h5>
          <CheckList checks={blockers} />
        </Alert>
        <Alert variant={warnings.length > 0 ? 'warning' : 'secondary'} className="mb-0">
          <h5>
            Warnings
            {' '}
            (
            {warnings.length}
            )
          </h5>
          <CheckList checks={warnings} />
        </Alert>
      </Card.Body>
    </Card>
  );
};

const SyncCoverageTable = ({ report }: { report: MigrationReadinessReport }): ReactElement => {
  const rows = [
    {
      label: report.SeniorsAlias || 'Residents',
      synced: report.SyncedResidents,
      unsynced: report.UnsyncedResidents,
    },
    {
      label: report.StaffAlias || 'Staff',
      synced: report.SyncedStaff,
      unsynced: report.UnsyncedStaff,
    },
    {
      label: report.FamilyAlias || 'Family',
      synced: report.SyncedFamily,
      unsynced: report.UnsyncedFamily,
    },
  ];

  return (
    <Table responsive bordered hover>
      <thead>
        <tr>
          <th>Persona</th>
          <th>Synced</th>
          <th>Unsynced</th>
          <th>Coverage</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const total = row.synced + row.unsynced;
          const syncedPercent = percent(row.synced, total);
          return (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td>{row.synced.toLocaleString()}</td>
              <td>{row.unsynced.toLocaleString()}</td>
              <td style={{ minWidth: 220 }}>
                <ProgressBar
                  now={syncedPercent}
                  label={`${syncedPercent}%`}
                  variant={row.unsynced > 0 ? 'warning' : 'success'}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const IntegrationBanners = ({ report }: { report: MigrationReadinessReport }): ReactElement | null => {
  const banners = [
    { label: report.SeniorsAlias || 'Residents', value: report.SeniorIntegrationBanner },
    { label: report.StaffAlias || 'Staff', value: report.StaffIntegrationBanner },
    { label: report.FamilyAlias || 'Family', value: report.FamilyIntegrationBanner },
  ].filter((banner) => banner.value);

  if (banners.length === 0) return null;

  return (
    <Card className="mb-4">
      <Card.Header as="h5">Integration banners</Card.Header>
      <Card.Body>
        {banners.map((banner) => (
          <div className="mb-3" key={banner.label}>
            <strong>{banner.label}</strong>
            <pre className="mt-2 mb-0 p-3 bg-light border rounded text-wrap">
              {banner.value}
            </pre>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

const CaasMigrationChecksPage = (): ReactElement => {
  const [isSuper] = useHasRoles(['Super']);
  const { data: report, error, isLoading } = useMigrationReadinessReport();

  if (!isSuper) {
    return <Redirect to="/" />;
  }

  const title = 'CaaS Migration Checks';

  if (isLoading) {
    return (
      <div className="broadcast-forms Templates">
        <Helmet><title>{title}</title></Helmet>
        <Spinner
          animation="border"
          variant="primary"
          role="status"
          className="mx-auto d-block mt-4 flex-shrink-0"
        />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="broadcast-forms Templates">
        <Helmet><title>{title}</title></Helmet>
        <Alert variant="danger">{getErrorMessage(error)}</Alert>
      </div>
    );
  }

  const linkedUserPercent = percent(
    report.EligibleUserCount - report.UnlinkedUserCount,
    report.EligibleUserCount,
  );
  const migrationChecks = generateMigrationChecks(report);

  const accountDetails: DetailItem[] = [
    { label: 'Account', value: `${report.AccountName || 'Unnamed account'} (${report.AccountId})` },
    { label: 'Status', value: formatNullable(report.Status) },
    { label: 'Customer type', value: formatNullable(report.CustomerType) },
    { label: 'Created', value: formatDate(report.CreateDate) },
    {
      label: 'Login type',
      value: formatNullable(report.LoginType),
      tooltip: 'Account login mode, such as password or SAML. SAML-style login may need separate migration review.',
    },
    { label: 'Timezone ID', value: formatNullable(report.TimezoneId) },
    {
      label: 'Community name',
      value: formatNullable(report.CommunityName),
      tooltip: 'Name alias for the community, may be referenced via merge field in messaging content',
    },
    { label: 'Salesforce account ID', value: formatNullable(report.SalesForceAccountId) },
    { label: 'Not for profit', value: formatBoolean(report.IsNotForProfit) },
  ];

  const linkageDetails: DetailItem[] = [
    {
      label: 'V1 synced facility ID',
      value: formatNullable(report.V1SyncedFacilityId),
      tooltip: 'Go Icon facility ID linked to this VoiceFriend account, when configured.',
    },
    {
      label: 'V1 sync tenant ID',
      value: formatNullable(report.V1SyncTenantId),
      tooltip: 'Go Icon tenant ID used by the legacy V1 sync configuration.',
    },
    {
      label: 'Automatic linking enabled',
      value: formatBoolean(report.AutomaticLinkingEnabled),
      tooltip: 'Whether V1 sync is configured for automatic user linking instead of manual linking.',
    },
    {
      label: 'Eligible users',
      value: report.EligibleUserCount.toLocaleString(),
      tooltip: 'Users counted for linking review, excluding internal support and engineering-style test users.',
    },
    {
      label: 'Unlinked users',
      value: report.UnlinkedUserCount.toLocaleString(),
      tooltip: 'Eligible users without a Go Icon user association.',
    },
    { label: 'Unlinked summary', value: report.UnlinkedUserSummary },
  ];

  const phoneDetails: DetailItem[] = [
    {
      label: 'Unique sender number',
      value: formatBoolean(report.UniquePhoneNumber),
      tooltip: 'Whether the registered sender number is unique to this account or shared with another account.',
    },
    { label: 'SMS inbox enabled', value: formatBoolean(report.SmsInboxEnabled) },
    {
      label: 'Caller ID',
      value: formatNullable(report.CallerId),
      tooltip: 'Legacy caller ID configured on the account.',
    },
    {
      label: 'Transfer-to numbers',
      value: report.TransferToNumberCount.toLocaleString(),
      tooltip: 'Distinct phone numbers used as transfer destinations.',
    },
    {
      label: 'Registered phone numbers',
      value: report.RegisteredPhoneNumbers.length.toLocaleString(),
      tooltip: 'Sender numbers registered for the account.',
    },
  ];

  const accountSettings: DetailItem[] = [
    {
      label: 'Throttle setting',
      value: formatNullable(report.ThrottleSetting),
      tooltip: 'Legacy account-level throttling configuration.',
    },
    {
      label: 'Repeat message',
      value: formatBoolean(report.RepeatMessage),
      tooltip: 'Whether voice messages are configured to repeat.',
    },
    {
      label: 'TTS rate',
      value: formatNullable(report.TtsRate),
      tooltip: 'Text-to-speech speaking rate configured for the account.',
    },
    { label: 'API enabled', value: formatBoolean(report.ApiEnabled) },
    {
      label: 'Post-event report',
      value: formatBoolean(report.SendPostEventReport),
      tooltip: 'Whether legacy communications sends reports after events complete.',
    },
    {
      label: 'Post-event recipients',
      value: formatNullable(report.PostEventEmailRecipients),
      tooltip: 'Email recipients configured for post-event reports.',
    },
    {
      label: 'Enterprise reporting',
      value: formatBoolean(report.EnterpriseReportingEnabled),
      tooltip: 'Whether enterprise reporting functionality is enabled for this account.',
    },
    {
      label: 'Broadcast greeting/farewell',
      value: formatBoolean(report.BroadcastGreetingFarewellEnabled),
      tooltip: 'Whether account-level greeting and farewell messages are enabled for broadcasts.',
    },
    {
      label: 'On-demand greeting/farewell',
      value: formatBoolean(report.OnDemandGreetingFarewellEnabled),
      tooltip: 'Whether account-level greeting and farewell messages are enabled for on-demand calls.',
    },
  ];

  const contentCounts: CountItem[] = [
    {
      label: 'Future scheduled broadcasts',
      value: report.FutureScheduledBroadcastsCount,
      tooltip: 'Future scheduled or queued broadcast calendar events.',
    },
    { label: 'Broadcast templates', value: report.BroadcastTemplateCount },
    { label: 'Marketing templates', value: report.MarketingTemplateCount },
    {
      label: 'Library content',
      value: report.LibraryContentCount,
      tooltip: 'Library or content templates configured in legacy communications.',
    },
    {
      label: 'Total automatic events',
      value: report.TotalAutomaticEventsCount,
      tooltip: 'Compliance and lifedate automatic events configured for the account.',
    },
    {
      label: 'Active automatic events',
      value: report.ActiveAutomaticEventsCount,
      tooltip: 'Automatic events still active based on stop rules.',
    },
    {
      label: 'SMS auto replies',
      value: report.SmsAutoRepliesCount,
      tooltip: 'Keyword-based SMS auto-reply rules configured for SMS inbox.',
    },
    {
      label: 'Virtual meetings',
      value: report.VirtualMeetingsCount,
      tooltip: 'Legacy virtual meeting usage.',
    },
    {
      label: 'Appointment reminders',
      value: report.AppointmentRemindersCount,
      tooltip: 'Appointment reminder configurations or usage in legacy communications.',
    },
    { label: 'Appointment reminder templates', value: report.AppointmentReminderTemplateCount },
    {
      label: 'Wellness checks',
      value: report.WellnessChecksCount,
      tooltip: 'Legacy wellness check usage.',
    },
    {
      label: 'Inactive periods',
      value: report.InactivePeriodCount,
      tooltip: 'Inactive-period rules configured for the account.',
    },
    {
      label: 'Do-not-call windows',
      value: report.DoNotCallBetweenCount,
      tooltip: 'Configured windows when calls should not be placed.',
    },
    {
      label: 'Phone simple surveys',
      value: report.PhoneSimpleSurveyCount,
      tooltip: 'Phone surveys that collect responses using keypresses.',
    },
    {
      label: 'Activity attendance',
      value: report.ActivityAttendanceCount,
      tooltip: 'Legacy activity attendance usage for tracking in-person activity participation.',
    },
    {
      label: 'Accushield templates',
      value: report.AccushieldTemplateCount,
      tooltip: 'Templates associated with Accushield workflows.',
    },
    {
      label: 'Feedback360 surveys',
      value: report.Feedback360SurveyCount,
      tooltip: 'Recent surveys sent over email or SMS.',
    },
    {
      label: 'Enterprise Feedback360 surveys',
      value: report.EnterpriseFeedback360SurveyCount,
      tooltip: 'Enterprise survey functionality usage.',
    },
    {
      label: 'Daily scheduled reports',
      value: report.DailyScheduledReportCount,
      tooltip: 'Legacy scheduled reports sent to one or more emails on a daily schedule.',
    },
    {
      label: 'Discharged patient records',
      value: report.DischargedPatientRecordCount,
      tooltip: 'Discharged patient records present in the account, indicating module usage.',
    },
    {
      label: 'On-demand usage',
      value: report.OnDemandUsageCount,
      tooltip: 'On-demand templates or usage configured for the account.',
    },
    {
      label: 'Reply forwarding',
      value: report.ReplyForwardingCount,
      tooltip: 'Reply recipients configured to forward incoming SMS content by email or SMS notification.',
    },
  ];

  const listItems: ListItem[] = [
    {
      label: 'Custom fields',
      count: report.CustomFieldCount,
      values: report.CustomFields,
      tooltip: 'Named custom fields configured or in use for this account.',
    },
    {
      label: 'API key usernames',
      count: report.ApiKeyCount,
      values: report.ApiKeyUsernames,
      tooltip: 'Usernames associated with configured API keys.',
    },
    {
      label: 'Lifedate fields',
      count: report.LifeDateFieldCount,
      values: report.LifeDateFields,
      tooltip: 'Lifedate-related fields configured for automatic event use.',
    },
    {
      label: 'Compliance date fields',
      count: report.ComplianceDateFieldCount,
      values: report.ComplianceDateFields,
      tooltip: 'Compliance-date fields configured for automatic event use.',
    },
    {
      label: 'Registered phone numbers',
      values: report.RegisteredPhoneNumbers,
      tooltip: 'Sender numbers registered for the account.',
    },
    {
      label: 'Transfer-to numbers',
      count: report.TransferToNumberCount,
      values: report.TransferToNumbers,
      tooltip: 'Distinct phone numbers used as transfer destinations.',
    },
    {
      label: 'Email sender domains',
      values: report.EmailSenderDomains,
      tooltip: 'Distinct domains extracted from template email sender addresses.',
    },
  ];

  return (
    <div className="broadcast-forms Templates">
      <Helmet><title>{title}</title></Helmet>

      <div className="mb-4">
        <h3>{title}</h3>
        <p className="text-muted mb-0">
          Read-only migration readiness snapshot for the currently selected account.
        </p>
      </div>

      <AutomatedChecksSummary
        blockers={migrationChecks.blockers}
        warnings={migrationChecks.warnings}
      />

      <Row>
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5">Account profile</Card.Header>
            <Card.Body>
              <DetailList items={accountDetails} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5">V1 linkage and user linking</Card.Header>
            <Card.Body>
              <DetailList items={linkageDetails} />
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Linked eligible users</span>
                  <span>
                    {linkedUserPercent}
                    %
                  </span>
                </div>
                <ProgressBar
                  now={linkedUserPercent}
                  variant={report.UnlinkedUserCount > 0 ? 'warning' : 'success'}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header as="h5">Sync coverage</Card.Header>
        <Card.Body>
          <SyncCoverageTable report={report} />
        </Card.Body>
      </Card>

      <Row>
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5">Phone and SMS</Card.Header>
            <Card.Body>
              <DetailList items={phoneDetails} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header as="h5">Account settings and feature toggles</Card.Header>
            <Card.Body>
              <DetailList items={accountSettings} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header as="h5">Messaging content and automation</Card.Header>
        <Card.Body>
          <CountGrid items={contentCounts} />
        </Card.Body>
      </Card>

      <Row>
        {listItems.map((item) => (
          <Col lg={6} className="mb-4" key={item.label}>
            <ListCard item={item} />
          </Col>
        ))}
      </Row>

      <IntegrationBanners report={report} />
    </div>
  );
};

export default CaasMigrationChecksPage;
